/**
 * Which cart lines conflict with a set of target delivery weeks.
 *
 * Task 13's reconcile dialog exists to NAME casualties before anything is removed
 * -- this is the pure predicate behind that: given the raw per-line availability
 * emitted by snippets/delivery-date-selector.liquid (key, title, start, end,
 * weeks) and the week code(s) a prospective delivery date would move the cart to,
 * return exactly the lines that can't come any of those weeks.
 *
 * Deliberately per-line rather than reusing the aggregate cartAllowedWeeks()
 * intersection in assets/delivery-date.js: that answers "is there ANY date that
 * works for the whole cart", this answers "WHICH lines specifically don't work",
 * which is what the dialog has to list.
 *
 * Fails open: an empty/invalid target-week list resolves to "nothing conflicts"
 * rather than "everything conflicts" -- a malformed target must never cause a
 * false-positive eviction of the entire cart.
 *
 * Pure and DOM-free so it's require()-able in node:test, mirroring
 * assets/cart-open-policy.js and assets/delivery-allowed-weeks.js. Its
 * dependencies (parseAvailability, availabilityFor) are themselves pure,
 * UMD-exported functions from assets/delivery-date-format.js -- required
 * directly under node:test, read off window.StockedDeliveryFormat in the browser
 * (theme.liquid loads delivery-date-format.js first).
 */
(function (root, factory) {
  var fmt = (typeof module !== 'undefined' && module.exports)
    ? require('./delivery-date-format.js')
    : root.StockedDeliveryFormat;
  var api = factory(fmt);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.StockedCartReconcile = api;
})(typeof self !== 'undefined' ? self : this, function (fmt) {
  // `targetWeeks` are delivery SLOTS: a week code, or the ISO of a special date.
  function conflictingLines(lines, targetWeeks) {
    var list = Array.isArray(lines) ? lines : [];
    var targets = Array.isArray(targetWeeks) ? targetWeeks.filter(Boolean) : [];
    // No valid target week to evaluate against -- fail open rather than treating
    // every line as a casualty (see file header).
    if (!targets.length) return [];
    return list.filter(function (line) {
      if (!line) return false;
      var availability = fmt.parseAvailability({
        startWeek: line.start,
        endWeek: line.end,
        weeks: line.weeks,
        dates: line.dates,
      });
      return !targets.some(function (w) {
        return fmt.availabilityFor(availability, w) === 'available';
      });
    });
  }

  // Content-addressed cache key for a set of conflicting lines against a target
  // week: sorted line keys + the week code. Used by revalidate()'s passive
  // reconciliation path (assets/delivery-date.js) to remember "the customer
  // already dismissed exactly this conflict this session" without re-prompting on
  // every page load / cart-drawer open. Deliberately content-addressed rather
  // than a boolean flag -- a DIFFERENT set of conflicting keys, or a DIFFERENT
  // target week, produces a different fingerprint and is correctly treated as a
  // new conflict, with no separate invalidation step required. Getting this
  // wrong in either direction is a real correctness bug (nagging forever, or
  // silently suppressing a genuinely new conflict), so it's covered by tests here
  // rather than left as DOM-adjacent glue in delivery-date.js.
  //
  // Pure and deterministic: sorts (so caller-supplied order never matters), never
  // throws on a missing/malformed weekCode (coerced to a string so the fingerprint
  // stays distinct and stable rather than blowing up).
  function reconcileFingerprint(conflicts, weekCode) {
    var keys = (Array.isArray(conflicts) ? conflicts : [])
      .map(function (c) { return c && c.key; })
      .filter(Boolean)
      .sort();
    return String(weekCode) + '|' + keys.join(',');
  }

  // Pure core of cartFrozenReason() (assets/delivery-date.js): why the cart
  // can't be left as-is, or null when it's fine. Slot-aware, so a healthy
  // catering cart on an open special date is NOT frozen (it must never be
  // auto-cleared on page load). `knownISOs` is every date currently orderable.
  function frozenReason(lines, iso, knownISOs, config) {
    var list = Array.isArray(lines) ? lines : [];
    if (!list.length) return null;
    if (!iso) return 'missing';
    if ((Array.isArray(knownISOs) ? knownISOs : []).indexOf(iso) === -1) return 'stale';
    if (conflictingLines(list, [fmt.slotOf(iso, config)]).length) return 'unavailable';
    return null;
  }

  return {
    conflictingLines: conflictingLines,
    reconcileFingerprint: reconcileFingerprint,
    frozenReason: frozenReason,
  };
});
